import { useCallback, useEffect, useState } from "react";
import { api, apiBlob, ApiError } from "@/lib/api";
import { Badge, Button, ErrorNote, Field, Panel, errorMessage, inputClass } from "@/components/ui";
import { fmtBytes, fmtDate } from "@/lib/format";
import type { Application, ApplicationStatus } from "@/lib/types";

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  new: "nueva",
  reviewing: "en revisión",
  contacted: "contactado",
  rejected: "descartado",
};

const STATUS_TONE: Record<ApplicationStatus, "ok" | "warn" | "mute" | "danger"> = {
  new: "ok",
  reviewing: "warn",
  contacted: "mute",
  rejected: "danger",
};

export function Applications({ jobId, onBack }: { jobId: number; onBack: () => void }) {
  const [items, setItems] = useState<Application[]>([]);
  const [jobTitle, setJobTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ApplicationStatus | "all">("all");

  const load = useCallback(async () => {
    try {
      const res = await api<{ job: { title: string }; items: Application[] }>(
        `/api/recruiter/jobs/${jobId}/applications`,
      );
      setItems(res.items);
      setJobTitle(res.job.title);
      setError(null);
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(id: number, body: Partial<Pick<Application, "status" | "notes">>) {
    try {
      await api(`/api/recruiter/applications/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setItems((prev) => prev.map((a) => (a.id === id ? { ...a, ...body } : a)));
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
    }
  }

  async function downloadCv(a: Application) {
    try {
      // El bucket de CV es privado: los bytes solo salen por esta ruta
      // autenticada, así que se descargan por fetch y se vuelcan a un
      // enlace temporal en vez de apuntar el navegador a una URL pública.
      const { blob, filename } = await apiBlob(`/api/recruiter/applications/${a.id}/cv`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
    }
  }

  const shown = filter === "all" ? items : items.filter((a) => a.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button variant="ghost" onClick={onBack}>
            ← volver
          </Button>
          <h2 className="mt-2 font-mono text-lg">Candidaturas · {jobTitle}</h2>
        </div>
        <div className="flex flex-wrap gap-1">
          {(["all", "new", "reviewing", "contacted", "rejected"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded border px-2 py-1 font-mono text-xs ${
                filter === s ? "border-accent text-accent" : "border-line text-mute hover:text-ink"
              }`}
            >
              {s === "all" ? "todas" : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <ErrorNote>{error}</ErrorNote>
      {loading && <p className="text-sm text-mute">Cargando…</p>}

      {!loading && shown.length === 0 && (
        <Panel>
          <p className="text-sm text-mute">
            {items.length === 0
              ? "Todavía no hay candidaturas para esta oferta."
              : "Ninguna candidatura con ese estado."}
          </p>
        </Panel>
      )}

      <ul className="space-y-3">
        {shown.map((a) => (
          <li key={a.id}>
            <Panel>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-mono text-base">{a.fullName || "(sin nombre)"}</h3>
                    <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                  </div>
                  <p className="mt-1 text-sm">
                    <a className="text-accent hover:underline" href={`mailto:${a.contactEmail}`}>
                      {a.contactEmail}
                    </a>
                    {a.phone ? <span className="text-mute"> · {a.phone}</span> : null}
                  </p>
                  {a.linkUrl && (
                    <p className="mt-1 text-sm">
                      <a
                        className="text-accent hover:underline"
                        href={a.linkUrl}
                        target="_blank"
                        // noopener: el destino lo aporta un tercero anónimo.
                        rel="noopener noreferrer nofollow"
                      >
                        {a.linkUrl}
                      </a>
                    </p>
                  )}
                  <p className="mt-1 text-xs text-mute">Recibida el {fmtDate(a.createdAt)}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {a.hasCv && (
                    <Button variant="ghost" onClick={() => void downloadCv(a)}>
                      CV ({fmtBytes(a.cvSize)})
                    </Button>
                  )}
                  <select
                    className={`${inputClass} w-auto`}
                    value={a.status}
                    onChange={(e) => void patch(a.id, { status: e.target.value as ApplicationStatus })}
                  >
                    {(Object.keys(STATUS_LABEL) as ApplicationStatus[]).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {a.coverLetter && (
                <div className="mt-3 rounded border border-line bg-bg p-3">
                  <p className="whitespace-pre-wrap text-sm text-ink">{a.coverLetter}</p>
                </div>
              )}

              <div className="mt-3">
                <Field label="nota interna">
                  <textarea
                    className={`${inputClass} min-h-16`}
                    defaultValue={a.notes ?? ""}
                    placeholder="Solo la ves tú."
                    // onBlur en vez de onChange: guardar en cada tecla
                    // sería un PATCH por pulsación.
                    onBlur={(e) => {
                      const next = e.target.value;
                      if (next !== (a.notes ?? "")) void patch(a.id, { notes: next });
                    }}
                  />
                </Field>
              </div>
            </Panel>
          </li>
        ))}
      </ul>
    </div>
  );
}
